import React, { useMemo } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withTranslation, WithTranslation } from 'react-i18next';
import classnames from 'classnames';
import Package from '../../components/package/package';
import ContentView from '../../components/contentView/contentView';
import ViewHeader from '../../components/viewHeader/viewHeader';
import getConfig from '../../config';
import { GlobalNotification } from '@jimdo/ui';
import FreeDomainFirstYearHint from '../../components/hints/freeDomainFirstYearHint';
import {
  yieldPackageSelection as yieldPackageSelectionAction,
  getPackagesAction,
} from '../../redux/actions/pricing';
import { PackageDescription } from '../../modules/getPackages';
import { AppState } from '../../redux/reducers';
import { PackageType } from '../../redux/reducers/pricing';
import { Prices } from '../../redux/actions/options';
import { AppDispatch, Map } from '../../definitions/types';

import DocumentTitle from '../../components/documentTitle/documentTitle';

import './pricingView.scss';
import { trackDWH } from '../../modules/tracking';

interface StateProps {
  prices?: Prices;
  rankingCoachAvailable?: boolean;
  error: string;
  lang: string;
  packages: Map<PackageDescription>;
}

interface DispatchProps {
  yieldPackageSelection: typeof yieldPackageSelectionAction;
  getPackages: typeof getPackagesAction;
}

type IPricingViewProps = StateProps & DispatchProps & WithTranslation;

const renderPackage = (
  props: IPricingViewProps,
  packages: Map<PackageDescription>,
  pkg: PackageType
): JSX.Element => {
  const price = props.prices ? props.prices[pkg] : null;

  return (
    <Package
      {...packages[pkg]}
      ribbonText={props.t('signup-flow.pricing.bestseller')}
      key={pkg}
      id={pkg}
      ctaText={props.t('signup-flow.pricing.cta-text')}
      toggleButtonText={props.t('signup-flow.pricing.toggle-button')}
      price={price}
      billingPeriod={props.t('signup-flow.pricing.billing-period.monthly')}
      onSelect={props.yieldPackageSelection}
    />
  );
};

export class PricingView extends React.Component<IPricingViewProps> {
  mountTime: number = Date.now();

  constructor(props: IPricingViewProps) {
    super(props);

    props.getPackages(props.t, props.lang, getConfig().country);
  }

  componentDidMount(): void {
    trackDWH('pricing-view', 'mount', { auto: true });
    this.mountTime = Date.now();
  }

  componentWillUnmount(): void {
    trackDWH('pricing-view', 'unmount', {
      auto: true,
      mountTimeMs: Date.now() - this.mountTime,
    });
  }

  public render(): JSX.Element {
    const { error, t, prices, packages, rankingCoachAvailable } = this.props;

    const domainPrice = prices ? prices.domain : '';
    const priceTiles = packages
      ? (Object.keys(packages) as Array<PackageType>).map((pkg) =>
          renderPackage(this.props, packages, pkg)
        )
      : null;
    const freeDomainTranslationKey = 'signup-flow.domain-first-year.asterisk-text';
    const rankingCoachNotAvailableTranslationKey =
      'signup-flow.ranking-coach-not-available.asterisk-text';

    return (
      <DocumentTitle title={t('signup-flow.document-title.pricing')}>
        <div>
          {error ? (
            <GlobalNotification type="warning" className="pricing-view__notification">
              {t(error)}
            </GlobalNotification>
          ) : undefined}
          <ContentView>
            <ViewHeader
              title={t('signup-flow.pricing.view.name')}
              headlineText={t('signup-flow.pricing.headline')}
              sublineText={t('signup-flow.pricing.description')}
            />
            <ul className="pricing-view__packages">{priceTiles}</ul>
              <FreeDomainFirstYearHint
                className="pricing-view__legalnote"
                domainPrice={domainPrice}
                translationKey={freeDomainTranslationKey}
              />
              {!rankingCoachAvailable && (
                <FreeDomainFirstYearHint
                  className={classnames('pricing-view__legalnote', 'pricing-view__legalnote--2')}
                  domainPrice={domainPrice}
                  translationKey={rankingCoachNotAvailableTranslationKey}
                />
            )}
          </ContentView>
        </div>
      </DocumentTitle>
    );
  }
}

const mapStateToProps = (appState: AppState): StateProps => {
  const {
    options: { prices, lang },
    error,
    ...state
  } = appState;

  return {
    prices: prices,
    error,
    lang,
    packages: state.pricing ? state.pricing.packages : null,
    rankingCoachAvailable: state.pricing ? state.pricing.rankingCoachAvailable : null,
  };
};

const mapDispatchToProps = (dispatch: AppDispatch): DispatchProps => {
  return bindActionCreators(
    {
      yieldPackageSelection: yieldPackageSelectionAction,
      getPackages: getPackagesAction,
    },
    dispatch
  );
};

const TranslatedPricingView = withTranslation()(PricingView);

const ConnectedPricingView = (): JSX.Element => {
  const stateProps = useSelector(mapStateToProps, shallowEqual);
  const dispatch = useDispatch<AppDispatch>();
  const dispatchProps = useMemo(() => mapDispatchToProps(dispatch), [dispatch]);

  return <TranslatedPricingView {...stateProps} {...dispatchProps} />;
};

export default React.memo(ConnectedPricingView);
